import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, Button, Box, CircularProgress, Alert, Typography, Pagination, Stack,
    ToggleButtonGroup, ToggleButton
} from "@mui/material";
import PageContainer from "../layout/PageContainer";
import useSearchStats from "../../hooks/useSearchStats";
import useSearchTrends from "../../hooks/useSearchTrends";

// Recharts
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";

const fmtDE = new Intl.DateTimeFormat("de-DE", {
    year: "numeric", month: "2-digit", day: "2-digit"
});

function ChartBlock() {
    const { days, setDays, items, loading, error, fetchTrends } = useSearchTrends(30);

    const chartData = React.useMemo(() => {
        // recharts data: { dateLabel, count }
        return items.map((it) => ({
            ...it,
            dateLabel: fmtDE.format(new Date(it.date + "T00:00:00Z")),
        }));
    }, [items]);

    const handleRange = (_e, value) => {
        if (!value) return;
        setDays(value);
        fetchTrends(value);
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6">Suchanfragen pro Tag</Typography>
                <ToggleButtonGroup
                    value={days}
                    exclusive
                    onChange={handleRange}
                    size="small"
                >
                    <ToggleButton value={7}>Letzte 7 Tage</ToggleButton>
                    <ToggleButton value={30}>Letzte 30 Tage</ToggleButton>
                    <ToggleButton value={90}>Letzte 90 Tage</ToggleButton>
                    <ToggleButton value={365}>Letztes Jahr</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ height: 300 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip
                                formatter={(value, name) => [value, name === "count" ? "Anfragen" : name]}
                                labelFormatter={(label) => label}
                            />
                            <Line type="monotone" dataKey="count" dot={false} strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Paper>
    );
}

export default function SearchStatsPage() {
    // ... dein bestehender Code aus der vorherigen Version ...
    // (unverändert; EINZIG: ChartBlock oben einfügen)
    const {
        items, total, limit, offset, q,
        setLimit, setOffset, setQ,
        loading, error, fetchStats
    } = useSearchStats({ limit: 25, offset: 0 });

    const pageCount = Math.max(1, Math.ceil(total / limit));
    const page = Math.floor(offset / limit) + 1;

    const handleRefresh = () => fetchStats({});
    const handleSearch = () => fetchStats({ offset: 0, q });
    const handleKeyPress = (e) => { if (e.key === "Enter") handleSearch(); };
    const handleChangePage = (_e, newPage) => {
        const newOffset = (newPage - 1) * limit;
        fetchStats({ offset: newOffset });
    };
    const handleChangeLimit = (newLimit) => {
        setLimit(newLimit);
        fetchStats({ limit: newLimit, offset: 0 });
    };

    const formatCreatedAt = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        if (isNaN(d)) return value;
        return new Intl.DateTimeFormat("de-DE", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(d);
    };

    return (
        <PageContainer
            title="Such-Statistiken"
            breadcrumbs={["Home", "Einstellungen", "Statistiken"]}
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
                        Aktualisieren
                    </Button>
                </Box>
            }
        >
            {/* Chart oben */}
            <ChartBlock />

            {/* Filter / Suche für die Tabelle */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                    <TextField
                        label="Filter (User / Query)"
                        placeholder="z.B. felix oder instagram"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={handleKeyPress}
                        fullWidth
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={loading}>
                        Suchen
                    </Button>
                    <TextField
                        label="Pro Seite"
                        type="number"
                        value={limit}
                        onChange={(e) => handleChangeLimit(Math.max(5, Math.min(200, Number(e.target.value) || 25)))}
                        sx={{ width: 120 }}
                    />
                </Stack>
            </Paper>

            {/* Fehler */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={handleRefresh}>Erneut laden</Button>}
                >
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 520, overflowY: "auto", overflowX: "auto" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width={90}>ID</TableCell>
                                <TableCell width={220}>User</TableCell>
                                <TableCell sx={{ minWidth: 300, wordBreak: "break-word" }}>Query</TableCell>
                                <TableCell width={120} align="right">Angefordert</TableCell>   {/* NEW */}
                                <TableCell width={120} align="right">Geliefert</TableCell>     {/* NEW */}
                                <TableCell width={220}>Zeitpunkt</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((row) => {
                                const orig = Number.isFinite(row.original_count) ? row.original_count : null;
                                const ret  = Number.isFinite(row.returned_count) ? row.returned_count : null;
                                // optional: Filterquote (wie viel weggefiltert wurde)
                                const filtered = (orig != null && ret != null && orig > 0) ? Math.max(0, orig - ret) : null;
                                const filteredPct = (filtered != null && orig) ? Math.round((filtered / orig) * 100) : null;
                                console.log(items)
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography component="span" sx={{ fontFamily: "monospace" }}>{row.id}</Typography>
                                        </TableCell>
                                        <TableCell>{row.user_name || "—"}</TableCell>
                                        <TableCell>
                                            <Typography component="span" sx={{ fontFamily: "monospace", wordBreak: "break-word" }}>
                                                {row.query_text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {orig != null ? orig : "—"}
                                        </TableCell>

                                        <TableCell align="right" title={filteredPct != null ? `Gefiltert: ${filtered} (${filteredPct}%)` : ""}>
                                            {ret != null ? ret : "—"}
                                        </TableCell>

                                        <TableCell>{formatCreatedAt(row.created_at)}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {!items.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        Keine Einträge gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Pagination */}
            {!!total && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination count={pageCount} page={page} onChange={handleChangePage} />
                </Box>
            )}
        </PageContainer>
    );
}
